'use client'

import { useRef } from 'react'
import { prefersReducedMotion } from './FlyingChip'
import { golferThoughtCopy } from './golferThoughtCopy'
import type { RevealedRoundType } from './livePutts'
import {
  THOUGHT_LIFE_MS,
  alwaysWorthALine,
  pickThought,
  rateFor,
  thoughtBudget,
  weatherMoment,
  type ThoughtMoment
} from './golferThought'
import { PUTTS_PER_ROUND, type PuttMark, type Translate } from './tileViewModel'

/**
 * B40 — which thought the golfer is having, if any.
 *
 * IT RIDES THE BOARD'S OWN BEAT. The moment is derived from `livePutts` — the
 * same value that drives the card's ring row and the avatar's reaction push-in —
 * inside the same render, so the bubble lands on the frame the ball does. A
 * timer of its own would arrive a couple of hundred milliseconds off and the
 * whole illusion (that he is reacting to the ball rather than to a clock) would
 * go with it.
 *
 * All the STATE is here and none of it is in the pure module: whether this round
 * has already spoken, which line it was, and whether the round before it spoke.
 * Refs rather than state, because none of it should cause a render — the bubble
 * appears because the putts changed, not because a bookkeeping flag did.
 */
export interface GolferThought {
  /** The localised line. */
  text: string
  /** The moment it belongs to — the bubble is keyed on it so each one re-enters. */
  moment: ThoughtMoment
}

export interface GolferThoughtInput {
  /** The round. A new one resets everything below. */
  roundKey: string | undefined
  /** Something stable about the golfer: his name is what a player knows him by. */
  golferKey: string | undefined
  /** The putts on screen — `phase.livePutts`, the board's own. */
  marks: readonly PuttMark[]
  /** True once the settlement is up: the round is over and he has stopped. */
  settled: boolean
  /** True while the board is between the close and the first putt. */
  teeingOff: boolean
  /**
   * The round's make chance, 0..1 — how likely each putt was to drop. It is what
   * decides whether a round is HARD enough to be worth talking through
   * (`thoughtBudget`): a 25 % golfer holing anything is worth a word, a 70 %
   * golfer holing the first is not.
   */
  makeRate?: number
  /**
   * THE REVEALED round type, and nothing else will do.
   *
   * It is the value the putt rows carry, which arrives mid-clip seconds past the
   * bet cutoff and is the same signal the board resolves its own Frost/Fire
   * tiles from. Frost and Fire are a MARKET players stake money on, so a line
   * drawn from the type before it is public would hand one player the answer to
   * a bet others are still making — see `golferThought`'s own header. The type is
   * the only door into that pool, and there is no other.
   */
  revealedType?: RevealedRoundType
  t: Translate
}

/**
 * The moment the putts on screen describe, or nothing.
 *
 * Pure, and deliberately the same read `liveStatus.nearMiss` makes — two in and
 * the third missed is the near miss, and it is recognised here from the same
 * marks rather than passed in, so the bubble and the board's own sigh can never
 * disagree about which round it was.
 */
export function thoughtMomentsFor(input: {
  marks: readonly PuttMark[]
  teeingOff: boolean
  revealedType?: RevealedRoundType
}): ThoughtMoment[] {
  const marks = input.marks
  if (marks.length === 0) return input.teeingOff ? ['pre'] : []

  const last = marks[marks.length - 1]
  if (last === undefined) return []
  let holed = 0
  for (const mark of marks) if (mark === 'holed') holed++

  let ball: ThoughtMoment
  if (last === 'missed') {
    // Two in and the third lipping out. The most characterful line in the pool,
    // and the one moment the board already draws a beat for.
    ball = marks.length === PUTTS_PER_ROUND && holed === PUTTS_PER_ROUND - 1 ? 'nearMiss' : 'miss'
  } else if (holed === 1) ball = 'in1'
  else if (holed === 2) ball = 'in2'
  else ball = 'in3'

  // THE WEATHER, on the beat the TILES resolve it. The revealed type rides the
  // putt ROWS, and a row's mark waits out the viewer's playout hold while the
  // type does not — so the type is in hand before the first mark is drawn. The
  // window is widened to the first TWO balls anyway: if a render ever saw the
  // mark before the type, the beat would otherwise have passed for good and the
  // line could never fire. Widening it is safe; moving it earlier would not be,
  // and it does not — it is still strictly after the reveal.
  //
  // NEVER the third ball, and that is what keeps it clear of the moments that
  // always get a line: the payoff and the heartbreak both happen there, where
  // this branch cannot reach.
  const weather = marks.length < PUTTS_PER_ROUND ? weatherMoment(input.revealedType) : undefined

  // The weather leads when it is available: it is a once-a-round observation and
  // the ball reactions get every other beat. The caller walks the list and takes
  // the first this round has not already spoken at.
  return weather === undefined ? [ball] : [weather, ball]
}

/** The single best moment, for callers that only want one. */
export function thoughtMomentFor(input: {
  marks: readonly PuttMark[]
  teeingOff: boolean
  revealedType?: RevealedRoundType
}): ThoughtMoment | undefined {
  return thoughtMomentsFor(input)[0]
}

interface ThoughtMemory {
  roundKey?: string
  /** Every line this round has used. He does not repeat himself inside a round. */
  used: string[]
  /** …and every MOMENT it has spoken at. One line each — see `ThoughtInput`. */
  moments: ThoughtMoment[]
  /** The line on screen now, if there is one, and when it went up. */
  lineId?: string
  text?: string
  moment?: ThoughtMoment
  shownAtMs?: number
  /** Whether the round BEFORE this one spoke, so two never land back to back. */
  spokeLastRound: boolean
}

export function useGolferThought(input: GolferThoughtInput): GolferThought | undefined {
  const memory = useRef<ThoughtMemory>({ used: [], moments: [], spokeLastRound: false })
  const { roundKey, golferKey } = input

  if (roundKey === undefined || golferKey === undefined) return undefined

  // A new round: everything resets, and whether the last one spoke is carried
  // over as the cooldown.
  if (memory.current.roundKey !== roundKey) {
    memory.current = {
      roundKey,
      used: [],
      moments: [],
      spokeLastRound: memory.current.used.length > 0
    }
  }

  // He stops when the round does. The settled board belongs to the result.
  if (input.settled) return undefined

  const candidates = thoughtMomentsFor({
    marks: input.marks,
    teeingOff: input.teeingOff,
    ...(input.revealedType === undefined ? {} : { revealedType: input.revealedType })
  })
  if (candidates.length === 0) return undefined

  // A bubble already up: hold it for its own life, then let it go. The HOLD is
  // what keeps it from flickering between renders, and the release is what lets
  // a round that earned a second line have one.
  const now = Date.now()
  const shownAt = memory.current.shownAtMs
  const bubbleUp = shownAt !== undefined && now - shownAt < THOUGHT_LIFE_MS
  if (bubbleUp) {
    const held = memory.current.text
    const heldMoment = memory.current.moment
    if (held === undefined || heldMoment === undefined) return undefined
    return { text: held, moment: heldMoment }
  }

  // A viewer who asked for less motion is not asked to read a bubble that
  // appears and vanishes on its own schedule.
  if (prefersReducedMotion()) return undefined

  const budget = thoughtBudget(input.makeRate === undefined ? {} : { makeRate: input.makeRate })

  // The candidates in priority order — the weather first where it applies, then
  // the ball. The first this round has not already spoken at wins; the picker
  // refuses the rest on `usedMoments` anyway, so this is the same answer either
  // way and simply avoids spending a draw to get it.
  for (const moment of candidates) {
    if (memory.current.moments.indexOf(moment) > -1) continue

    // The randomness is injected into the pure picker; this is where it is
    // drawn. Two independent draws — one decides whether he speaks and from
    // which pool, the other which line — so a habit cannot also bias the rate.
    const line = pickThought({
      moment,
      golferKey,
      usedLineIds: memory.current.used,
      usedMoments: memory.current.moments,
      // HOW UNLIKELY the thing that just happened was, turned into how likely he
      // is to remark on it (`rateFor`). The pre-putt moment ignores both of
      // these and takes its own flat constant, which is what keeps a line before
      // a ball is struck incapable of carrying anything about the round.
      rate: rateFor({
        moment,
        marks: input.marks,
        ...(input.makeRate === undefined ? {} : { makeRate: input.makeRate })
      }),
      ...(memory.current.lineId === undefined ? {} : { lastLineId: memory.current.lineId }),
      ...(memory.current.spokeLastRound ? { spokeLastRound: true } : {}),
      spokenThisRound: memory.current.used.length,
      budget,
      roll: Math.random(),
      pick: Math.random()
    })
    if (line === undefined) {
      // The moment was OFFERED and declined. Mark it spoken for anyway, or the
      // same roll is taken again on the next render for as long as the condition
      // holds — which is seconds, and is how the tee-off window used to produce
      // two lines in a row.
      memory.current.moments = memory.current.moments.concat([moment])
      continue
    }

    const text = golferThoughtCopy(line.id, input.t)
    if (text === undefined || text.length === 0) continue

    memory.current.lineId = line.id
    memory.current.text = text
    memory.current.moment = moment
    memory.current.shownAtMs = now
    memory.current.used = memory.current.used.concat([line.id])
    memory.current.moments = memory.current.moments.concat([moment])
    return { text, moment }
  }
  return undefined
}

/** Kept for readers of the module: which moments ignore the budget. */
export { alwaysWorthALine }
